import { useState, useEffect } from "react";
import { Mail, Phone, MapPin, Linkedin, Send, Check, AlertCircle, Shield, Radio, Eye, BarChart3, ChevronRight, Building, Users, Briefcase, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { SEOHead } from "../components/seo/SEOHead";
import { cn } from "@/lib/utils";

// Category configuration with sub-categories and contextual info
const categoryConfig = {
  solutions: {
    label: "Solutions",
    icon: Shield,
    description: "Discuss your operational requirements",
    color: "border-foreground/30",
    subCategories: [
      { 
        value: "defence-cuas", 
        label: "Defence & Counter-UAS",
        description: "Mission systems and analytical technology",
        info: "Discuss counter-UAS capability assessment, mission integration, or autonomous technology evaluation.",
        icon: Shield,
      },
      { 
        value: "protective-security", 
        label: "Protective Security",
        description: "Helais Sentinel for site protection",
        info: "Scope a pilot for continuous situational awareness and operator-controlled aerial observation.",
        icon: Eye,
      },
      { 
        value: "public-safety", 
        label: "Public Safety",
        description: "Mission orchestration for emergency response",
        info: "Discuss mission integration for aerial support workflows and multi-asset coordination.",
        icon: Radio,
      },
      { 
        value: "analytics", 
        label: "Operational Analytics",
        description: "UrbanAnalytix for civil intelligence",
        info: "Scope an analytics pilot with defined deliverables from your existing infrastructure.",
        icon: BarChart3,
      },
    ],
  },
  platforms: {
    label: "Platforms & Technology",
    icon: BarChart3,
    description: "Technical integration enquiries",
    color: "border-foreground/30",
    subCategories: [
      { 
        value: "uavsimnet", 
        label: "UAVSimNet",
        description: "Simulation & decision support",
        info: "Scope an assessment for capability evaluation, test planning, or procurement comparison.",
        icon: Eye,
      },
      { 
        value: "ai-av", 
        label: "AI-AV",
        description: "Vision-based autonomous guidance",
        info: "Discuss integration of vision-based guidance designed to operate without GPS.",
        icon: Eye,
      },
      { 
        value: "helais", 
        label: "Helais",
        description: "Mission orchestration platform",
        info: "Discuss mission orchestration, asset coordination, or aerial operations integration.",
        icon: Radio,
      },
      { 
        value: "partnership", 
        label: "Development Partnership",
        description: "Licensing & collaboration",
        info: "Explore partnership opportunities, design asset licensing, or development collaboration.",
        icon: Users,
      },
    ],
  },
  corporate: {
    label: "Corporate",
    icon: Building,
    description: "Business & investment enquiries",
    color: "border-foreground/30",
    subCategories: [
      { 
        value: "investment", 
        label: "Investment",
        description: "Investment opportunities",
        info: "Learn about investment opportunities and strategic partnerships with The RLK Group.",
        icon: Briefcase,
      },
      { 
        value: "media", 
        label: "Media & Press",
        description: "Media enquiries & press",
        info: "Media enquiries, press releases, and interview requests.",
        icon: Users,
      },
      { 
        value: "careers", 
        label: "Careers",
        description: "Career opportunities",
        info: "Explore career opportunities at The RLK Group across engineering, autonomy, and business development.",
        icon: Users,
      },
    ],
  },
  general: {
    label: "General Enquiry",
    icon: HelpCircle,
    description: "Other enquiries",
    color: "border-border",
    subCategories: [
      { 
        value: "general", 
        label: "General Question",
        description: "General enquiry",
        info: "For all other enquiries not covered by the categories above.",
        icon: HelpCircle,
      },
    ],
  },
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    category: "",
    subCategory: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1 = category, 2 = details

  // Get current category config
  const currentCategory = formData.category ? categoryConfig[formData.category] : null;
  const currentSubCategory = currentCategory?.subCategories.find(s => s.value === formData.subCategory);

  // Reset sub-category when category changes
  useEffect(() => {
    if (formData.category) {
      setFormData(prev => ({ ...prev, subCategory: "" }));
    }
  }, [formData.category]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const selectCategory = (categoryKey) => {
    setFormData(prev => ({ ...prev, category: categoryKey, subCategory: "" }));
  };

  const selectSubCategory = (subCategoryValue) => {
    setFormData(prev => ({ ...prev, subCategory: subCategoryValue }));
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Map to backend format
      const subjectMap = {
        'ai-av': 'defence',
        'helais': 'defence',
        'integration': 'defence',
        'technical': 'defence',
        'urbananalytix': 'technologies',
        'partnership': 'technologies',
        'investment': 'general',
        'media': 'general',
        'careers': 'general',
        'general': 'general',
      };

      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || "",
          subject: subjectMap[formData.subCategory] || 'general',
          message: `[${currentCategory?.label} - ${currentSubCategory?.label}]${formData.company ? `\nCompany: ${formData.company}` : ''}\n\n${formData.message}`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to submit form');
      }

      setIsSubmitted(true);
      toast.success("Message sent successfully", {
        description: "Thank you for your enquiry. We'll be in touch shortly.",
      });

      // Reset form after delay
      setTimeout(() => {
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          category: "",
          subCategory: "",
          message: "",
        });
        setIsSubmitted(false);
        setStep(1);
      }, 3000);
      
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || 'An error occurred. Please try again.');
      toast.error("Submission failed", {
        description: err.message || "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact | The RLK Group"
        description="Get in touch with The RLK Group to discuss defence AI systems, autonomous platforms, and technology solutions."
        path="/contact"
      />
      
      <div className="relative pt-20">
        {/* Hero */}
        <section className="section-padding relative overflow-hidden">
          <div className="absolute inset-0 grid-pattern-fine" />
          <div className="absolute inset-0 noise-texture" />
          
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
            <div className="max-w-3xl">
              <h1 className="text-display text-foreground mb-6 opacity-0 animate-fade-up">
                Contact
              </h1>
              <p className="text-subtitle opacity-0 animate-fade-up delay-100">
                Get in touch with The RLK Group to discuss how our platforms can address your requirements.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="section-padding-lg relative">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid gap-16 lg:grid-cols-[1fr_320px]">
              {/* Form */}
              <div className="card-premium p-8 lg:p-10">
                {/* Progress indicator */}
                <div className="flex items-center gap-2 mb-8">
                  <div className={cn(
                    "flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium transition-colors",
                    step >= 1 ? "bg-foreground text-background" : "bg-muted text-muted-foreground"
                  )}>
                    1
                  </div>
                  <div className={cn(
                    "h-0.5 w-12 transition-colors",
                    step >= 2 ? "bg-foreground" : "bg-border"
                  )} />
                  <div className={cn(
                    "flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium transition-colors",
                    step >= 2 ? "bg-foreground text-background" : "bg-muted text-muted-foreground"
                  )}>
                    2
                  </div>
                </div>

                {/* Step 1: Category Selection */}
                {step === 1 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-title text-foreground mb-2">How can we help?</h2>
                      <p className="text-body text-muted-foreground">Select a category to help us direct your enquiry.</p>
                    </div>

                    {/* Category Cards */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      {Object.entries(categoryConfig).map(([key, config]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => selectCategory(key)}
                          data-testid={`category-${key}`}
                          className={cn(
                            "flex items-start gap-4 p-4 rounded border text-left transition-all duration-200",
                            formData.category === key
                              ? "border-foreground bg-foreground/5"
                              : "border-border hover:border-foreground/30 bg-card"
                          )}
                        >
                          <div className={cn(
                            "h-10 w-10 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                            formData.category === key
                              ? "border-foreground bg-foreground/10"
                              : "border-border bg-muted/50"
                          )}>
                            <config.icon className="h-5 w-5 text-foreground/70" />
                          </div>
                          <div>
                            <span className="text-label text-foreground block">{config.label}</span>
                            <span className="text-sm text-muted-foreground">{config.description}</span>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Sub-category Selection */}
                    {formData.category && (
                      <div className="space-y-4 pt-4 border-t border-border animate-fade-up">
                        <p className="text-label text-foreground">
                          What specifically are you interested in?
                        </p>
                        <div className="grid gap-3">
                          {currentCategory?.subCategories.map((sub) => (
                            <button
                              key={sub.value}
                              type="button"
                              onClick={() => selectSubCategory(sub.value)}
                              data-testid={`subcategory-${sub.value}`}
                              className={cn(
                                "flex items-center justify-between p-4 rounded border transition-all duration-200 group",
                                formData.subCategory === sub.value
                                  ? "border-foreground bg-foreground/5"
                                  : "border-border hover:border-foreground/30 bg-card"
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <div className={cn(
                                  "h-8 w-8 rounded border flex items-center justify-center transition-colors",
                                  formData.subCategory === sub.value
                                    ? "border-foreground bg-foreground/10"
                                    : "border-border bg-muted/50"
                                )}>
                                  <sub.icon className="h-4 w-4 text-foreground/70" />
                                </div>
                                <div className="text-left">
                                  <span className="text-sm font-medium text-foreground block">{sub.label}</span>
                                  <span className="text-xs text-muted-foreground">{sub.description}</span>
                                </div>
                              </div>
                              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Contact Details */}
                {step === 2 && (
                  <div className="space-y-6">
                    {/* Selected category info */}
                    {currentSubCategory && (
                      <div className="p-4 rounded border border-foreground/20 bg-foreground/5 animate-fade-up">
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded border border-foreground/30 bg-foreground/10 flex items-center justify-center flex-shrink-0">
                            <currentSubCategory.icon className="h-5 w-5 text-foreground/70" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-muted-foreground">{currentCategory?.label}</span>
                              <ChevronRight className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs text-foreground font-medium">{currentSubCategory.label}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">{currentSubCategory.info}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setStep(1)}
                          className="text-xs text-muted-foreground hover:text-foreground mt-3 transition-colors"
                        >
                          ← Change selection
                        </button>
                      </div>
                    )}

                    <div>
                      <h2 className="text-title text-foreground mb-2">Your Details</h2>
                      <p className="text-body text-muted-foreground">Complete the form and we'll respond as soon as possible.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid gap-6 sm:grid-cols-2">
                        <div className="space-y-2">
                          <label htmlFor="name" className="text-label">Name *</label>
                          <input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Your name"
                            data-testid="input-name"
                            className="w-full h-11 px-4 rounded border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-foreground/30 transition-colors"
                          />
                        </div>
                        <div className="space-y-2">
                          <label htmlFor="email" className="text-label">Email *</label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="your@email.com"
                            data-testid="input-email"
                            className="w-full h-11 px-4 rounded border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-foreground/30 transition-colors"
                          />
                        </div>
                      </div>

                      <div className="grid gap-6 sm:grid-cols-2">
                        <div className="space-y-2">
                          <label htmlFor="company" className="text-label">Company / Organisation</label>
                          <input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your company"
                            data-testid="input-company"
                            className="w-full h-11 px-4 rounded border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-foreground/30 transition-colors"
                          />
                        </div>
                        <div className="space-y-2">
                          <label htmlFor="phone" className="text-label">Phone</label>
                          <input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+44 ..."
                            data-testid="input-phone"
                            className="w-full h-11 px-4 rounded border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-foreground/30 transition-colors"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="message" className="text-label">Message *</label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          placeholder="Tell us about your requirements..."
                          rows={6}
                          data-testid="input-message"
                          className="w-full px-4 py-3 rounded border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-foreground/30 transition-colors resize-none"
                        />
                      </div>

                      <p className="text-xs text-muted-foreground">
                        By submitting this form, you agree to our handling of your data in accordance with our privacy policy.
                      </p>

                      {error && (
                        <div className="flex items-center gap-2 p-3 rounded border border-red-500/30 bg-red-500/10 text-red-400 text-sm">
                          <AlertCircle className="h-4 w-4 flex-shrink-0" />
                          <span>{error}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          onClick={() => setStep(1)}
                          className="btn-secondary"
                          data-testid="back-btn"
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting || isSubmitted}
                          className="btn-primary flex-1 sm:flex-none disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="submit-btn"
                        >
                          {isSubmitting ? (
                            <span className="animate-pulse">Sending...</span>
                          ) : isSubmitted ? (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              Message Sent
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Send Message
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-title text-foreground mb-6">Contact Information</h2>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-label mb-1">Address</p>
                        <p className="text-sm text-muted-foreground">272 Bath Street<br />Glasgow</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center flex-shrink-0">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-label mb-1">Email</p>
                        <a
                          href="mailto:info@rlktech.co.uk"
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors block"
                        >
                          info@rlktech.co.uk
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center flex-shrink-0">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-label mb-1">Phone</p>
                        <a
                          href="tel:01413194813"
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          0141 319 4813
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="divider" />

                {/* Quick context based on selection */}
                {currentCategory && step === 1 && (
                  <div className="p-4 rounded border border-border bg-card animate-fade-up">
                    <div className="flex items-center gap-3 mb-3">
                      <currentCategory.icon className="h-5 w-5 text-foreground/70" />
                      <span className="text-label text-foreground">{currentCategory.label}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {currentCategory.description}
                    </p>
                  </div>
                )}

                <div>
                  <h3 className="text-label mb-4">LinkedIn</h3>
                  <a
                    href="https://www.linkedin.com/company/the-rlk-group/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded border border-border bg-muted/30 hover:border-foreground/20 transition-colors group"
                  >
                    <Linkedin className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      The RLK Group
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
